/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useState } from 'react';
import { FSF_BRAND } from '../fsfPages.generated';
import ContactIcon from '../components/ContactIcon';
import PhotoLightbox from '../components/PhotoLightbox';
import TabsBlock from '../components/TabsBlock';
import { MOVING_IMAGES, MOVING_PHOTOS, MOVING_SERVICE_META } from '../data/siteData';

export function MovingCopy({ html, className = '' }) {
  if (!html) {
    return null;
  }

  return <div className={`fsf-moving-copy ${className}`.trim()} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function MovingMedia({ photo, index, onOpen, className = '', loading = 'lazy' }) {
  if (!photo?.src) {
    return null;
  }

  return (
    <figure className={`fsf-moving-media ${className}`.trim()}>
      <button
        className="fsf-moving-media__button"
        type="button"
        onClick={() => onOpen(index)}
        aria-label={`Open ${photo.alt}`}
      >
        <img src={photo.src} alt={photo.alt} loading={loading} />
      </button>
    </figure>
  );
}

export function MovingServiceCard({ item, index }) {
  const meta = MOVING_SERVICE_META.find((service) => service.title === item.title) || MOVING_SERVICE_META[index];

  return (
    <article className="fsf-moving-service-card">
      <span className="fsf-moving-service-card__icon" aria-hidden="true">
        <ContactIcon name={meta?.icon || 'package'} />
      </span>
      <div className="fsf-moving-service-card__body">
        <p>{String(index + 1).padStart(2, '0')}</p>
        <h3>{item.title}</h3>
        <span>{meta?.summary || 'Practical moving and relocation support for household goods, personal effects, and special items.'}</span>
      </div>
    </article>
  );
}

export function getMovingTabs(page) {
  const tabsBlock = (page.blocks || []).find((block) => block?.type === 'tabs');

  return tabsBlock?.items || [];
}

export default function MovingPageSections({ page }) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(null);
  const introBlock = (page.blocks || []).find((block) => block?.type === 'html');
  const tabs = getMovingTabs(page);

  return (
    <>
      <div className="fsf-moving-story">
        <section className="fsf-moving-section fsf-moving-section--intro">
          <div className="fsf-moving-section__inner fsf-moving-intro">
            <div className="fsf-moving-intro__copy">
              <p className="fsf-section-kicker">Moving Division</p>
              <h2>Moving made easier, from Fiji to the wider Pacific.</h2>
              <MovingCopy html={introBlock?.html} className="fsf-moving-copy--quote" />
              <div className="fsf-moving-highlights" aria-label="Moving capabilities">
                <span>International</span>
                <span>Domestic</span>
                <span>Pets</span>
                <span>Vehicles</span>
                <span>Storage</span>
              </div>
            </div>
            <MovingMedia
              photo={MOVING_IMAGES.heroTruck}
              index={0}
              onOpen={setActivePhotoIndex}
              className="fsf-moving-media--hero"
              loading="eager"
            />
          </div>
        </section>

        <section className="fsf-moving-section fsf-moving-section--services">
          <div className="fsf-moving-section__inner">
            <div className="fsf-moving-section__header">
              <p className="fsf-section-kicker">Relocation Services</p>
              <h2>Household, personal, vehicle, and special-item moves</h2>
              <p>
                FSF Moving brings packing, forwarding, storage, clearance, and delivery support together so customers
                can relocate with a clearer plan and fewer loose ends.
              </p>
            </div>
            <div className="fsf-moving-service-grid">
              {tabs.map((item, index) => (
                <MovingServiceCard key={`${item.title}-${index}`} item={item} index={index} />
              ))}
            </div>
          </div>
        </section>

        <section className="fsf-moving-section fsf-moving-section--process">
          <div className="fsf-moving-section__inner fsf-moving-process">
            <MovingMedia photo={MOVING_IMAGES.warehouseBoxes} index={2} onOpen={setActivePhotoIndex} className="fsf-moving-media--storage" />
            <div className="fsf-moving-process__copy">
              <p className="fsf-section-kicker">How We Help</p>
              <h2>Plan it, protect it, move it with care</h2>
              <div className="fsf-moving-steps">
                <article>
                  <span>01</span>
                  <h3>Plan the move</h3>
                  <p>We help map the service mix, timing, documentation, and handling needs before the job starts.</p>
                </article>
                <article>
                  <span>02</span>
                  <h3>Pack and protect</h3>
                  <p>Household goods, personal effects, vehicles, and special items are prepared for transit or storage.</p>
                </article>
                <article>
                  <span>03</span>
                  <h3>Clear, store, deliver</h3>
                  <p>Our moving and forwarding teams coordinate storage, clearance, and delivery through final handover.</p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="fsf-moving-section fsf-moving-section--details">
          <div className="fsf-moving-section__inner">
            <div className="fsf-moving-section__header">
              <p className="fsf-section-kicker">Service Details</p>
              <h2>Choose a moving service to explore</h2>
            </div>
            <TabsBlock items={tabs} />
          </div>
        </section>

        <section className="fsf-moving-section fsf-moving-section--photos">
          <div className="fsf-moving-section__inner">
            <div className="fsf-moving-section__header">
              <p className="fsf-section-kicker">On The Move</p>
              <h2>People, vehicles, storage, and delivery in action</h2>
            </div>
            <div className="fsf-moving-photo-grid">
              <MovingMedia photo={MOVING_IMAGES.yardTeam} index={1} onOpen={setActivePhotoIndex} className="fsf-moving-media--wide" />
              <MovingMedia photo={MOVING_IMAGES.contactTruck} index={3} onOpen={setActivePhotoIndex} className="fsf-moving-media--tall" />
              <MovingMedia photo={MOVING_IMAGES.residentialMove} index={5} onOpen={setActivePhotoIndex} />
              <MovingMedia photo={MOVING_IMAGES.vehicleLoading} index={6} onOpen={setActivePhotoIndex} />
              <MovingMedia photo={MOVING_IMAGES.vehicleHandover} index={4} onOpen={setActivePhotoIndex} className="fsf-moving-media--wide" />
              <MovingMedia photo={MOVING_IMAGES.customsMove} index={7} onOpen={setActivePhotoIndex} />
            </div>
          </div>
        </section>

        <section className="fsf-moving-section fsf-moving-section--cta">
          <div className="fsf-moving-section__inner fsf-moving-cta">
            <div>
              <p className="fsf-section-kicker">Planning A Move?</p>
              <h2>Let FSF help make the next move feel manageable.</h2>
            </div>
            <div className="fsf-moving-cta__actions">
              <a className="fsf-button" href={FSF_BRAND.quoteUrl} target="_blank" rel="noopener noreferrer">Get a Quotation</a>
              <a className="fsf-button fsf-button--secondary" href="/contactus/">Contact FSF</a>
            </div>
          </div>
        </section>
      </div>

      <PhotoLightbox
        images={MOVING_PHOTOS}
        activeIndex={activePhotoIndex}
        setActiveIndex={setActivePhotoIndex}
        label="Freight Services Fiji moving photos"
      />
    </>
  );
}
